import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Power, X, Save, Pencil, Image as ImageIcon } from 'lucide-react';
import api from '../../api/axios';

const AdminJobsPage = () => {
    const [jobs, setJobs] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [currentId, setCurrentId] = useState(null);
    const [loading, setLoading] = useState(false);

    const [formData, setFormData] = useState({
        title: '',
        branch: 'Pachuca',
        category: 'Vacante',
        requirements: '',
        vacancies_count: 1,
        image_url: '' // <-- Nuevo campo para la imagen personalizada
    });

    useEffect(() => { fetchAllJobs(); }, []);

    const fetchAllJobs = async () => {
        try {
            const response = await api.get('/admin/jobs'); 
            setJobs(response.data);
        } catch (e) { 
            console.error(e); 
        }
    };

    const handleEditClick = (job) => {
        setFormData({
            title: job.title,
            branch: job.branch,
            category: job.category,
            requirements: job.requirements,
            vacancies_count: job.vacancies_count,
            image_url: job.image_url || '' // <-- Cargamos la imagen al editar
        });
        setCurrentId(job.id);
        setIsEditing(true);
        setIsModalOpen(true);
    };

    const handleSaveJob = async (e) => {
    e.preventDefault();
    
    // 🛡️ Blindaje: Validación básica de URL (opcional pero recomendado)
    if (formData.image_url && !formData.image_url.startsWith('http')) {
        alert("La URL de la imagen debe comenzar con http o https");
        return;
    }

    setLoading(true);
    try {
        if (isEditing) {
            await api.put(`/admin/jobs/${currentId}`, formData);
        } else {
            await api.post('/admin/jobs', formData);
        }
        closeModal();
        fetchAllJobs();
    } catch (e) {
        // Mejor manejo de error: Mostramos el detalle técnico si existe
        const msg = e.response?.data?.message || "Error al guardar la vacante.";
        alert(msg);
    } finally {
        setLoading(false);
    }
};

    const closeModal = () => {
        setIsModalOpen(false);
        setIsEditing(false);
        setCurrentId(null);
        setFormData({ 
            title: '', 
            branch: 'Pachuca', 
            category: 'Vacante', 
            requirements: '', 
            vacancies_count: 1,
            image_url: '' // <-- Limpiamos el campo al cerrar
        });
    };

    const handleToggle = async (id) => {
        await api.patch(`/admin/jobs/${id}/toggle`);
        fetchAllJobs();
    };

    const handleDelete = async (id) => {
        if (window.confirm("¿Realmente deseas eliminar esta vacante? No se podrá recuperar.")) {
            await api.delete(`/admin/jobs/${id}`);
            fetchAllJobs();
        }
    };

    return (
        <div className="p-8 space-y-8 relative">
            <div className="flex justify-between items-center">
                <h2 className="text-3xl font-black uppercase dark:text-white">
                    Gestión de <span className="text-[#CE010B]">Vacantes</span>
                </h2>
                <button 
                    onClick={() => { setIsEditing(false); setIsModalOpen(true); }} 
                    className="bg-[#CE010B] text-white px-6 py-3 rounded-2xl font-black uppercase text-xs flex items-center gap-2 transition-all shadow-lg hover:scale-105 active:scale-95"
                >
                    <Plus size={18} /> Nueva Vacante
                </button>
            </div>

            {/* GRID DE VACANTES */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {jobs.map(job => (
                    <div key={job.id} className="bg-white dark:bg-black/20 p-6 rounded-[2rem] border border-black/10 flex flex-col justify-between space-y-4 hover:shadow-xl transition-shadow">
                        <div>
                            <div className="flex justify-between items-start">
                                <span className="text-[9px] font-black uppercase p-2 bg-black/5 dark:bg-white/5 rounded-lg dark:text-white">{job.branch}</span>
                                <div className={`w-3 h-3 rounded-full ${job.is_active ? 'bg-green-500' : 'bg-red-500'} shadow-[0_0_8px_rgba(34,197,94,0.5)]`} />
                            </div>
                            <h4 className="text-xl font-black uppercase mt-2 dark:text-white leading-tight">{job.title}</h4>
                            <p className="text-[10px] font-black text-[#CE010B] uppercase">{job.category}</p>
                        </div>

                        {/* MINIATURA DE LA IMAGEN ELEGIDA */}
                        {job.image_url && (
                            <div className="h-20 w-full rounded-xl overflow-hidden border border-black/5 dark:border-white/5 bg-gray-100">
                                <img 
                                    src={job.image_url} 
                                    alt="preview" 
                                    className="w-full h-full object-cover grayscale hover:grayscale-0 transition-all"
                                    onError={(e) => { e.target.src = '/placeholder-job.png'; }} // 🛡️ Si falla, carga un placeholder
                                />
                            </div>
                        )}

                        <div className="flex gap-2 pt-4 border-t border-black/5 dark:border-white/5">
                            <button onClick={() => handleEditClick(job)} className="flex-1 p-3 bg-black/5 dark:bg-white/5 rounded-xl hover:bg-black/10 transition-all flex justify-center text-blue-500">
                                <Pencil size={18} />
                            </button>
                            <button onClick={() => handleToggle(job.id)} className="flex-1 p-3 bg-black/5 dark:bg-white/5 rounded-xl hover:bg-black/10 transition-all flex justify-center">
                                <Power size={18} className={job.is_active ? 'text-green-500' : 'text-gray-400'} />
                            </button>
                            <button onClick={() => handleDelete(job.id)} className="flex-1 p-3 bg-red-50 dark:bg-red-900/20 rounded-xl hover:bg-red-100 transition-all flex justify-center">
                                <Trash2 size={18} className="text-red-500" />
                            </button>
                        </div>
                    </div>
                ))}
            </div>

            {/* MODAL */}
            {isModalOpen && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
                    <div className="bg-white dark:bg-[#1A1A1A] w-full max-w-md rounded-[2.5rem] shadow-2xl p-8 space-y-6">
                        <div className="flex justify-between items-center">
                            <h3 className="text-2xl font-black uppercase dark:text-white">
                                {isEditing ? 'Editar' : 'Crear'} <span className="text-[#CE010B]">Vacante</span>
                            </h3>
                            <button onClick={closeModal} className="p-2 hover:bg-black/5 dark:hover:bg-white/10 rounded-full transition-colors">
                                <X size={24} className="dark:text-white" />
                            </button>
                        </div>

                        <form onSubmit={handleSaveJob} className="space-y-4">
                            <div className="space-y-1">
                                <label className="text-[10px] font-black uppercase ml-2 dark:text-white/50 tracking-widest">Título del Puesto</label>
                                <input required className="w-full px-5 py-3 rounded-2xl bg-black/5 dark:bg-white/5 dark:text-white outline-none font-bold focus:ring-2 ring-[#CE010B]/20" value={formData.title} onChange={(e) => setFormData({...formData, title: e.target.value})} />
                            </div>
                            
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-1">
                                    <label className="text-[10px] font-black uppercase ml-2 dark:text-white/50 tracking-widest">Sucursal</label>
                                    <select className="w-full px-5 py-3 rounded-2xl bg-black/5 dark:bg-white/5 dark:text-white outline-none font-bold" value={formData.branch} onChange={(e) => setFormData({...formData, branch: e.target.value})}>
                                        <option value="Pachuca">Pachuca</option>
                                        <option value="Toluca">Toluca</option>
                                        <option value="CDMX">CDMX</option>
                                    </select>
                                </div>
                                <div className="space-y-1">
                                    <label className="text-[10px] font-black uppercase ml-2 dark:text-white/50 tracking-widest">Tipo</label>
                                    <select className="w-full px-5 py-3 rounded-2xl bg-black/5 dark:bg-white/5 dark:text-white outline-none font-bold" value={formData.category} onChange={(e) => setFormData({...formData, category: e.target.value})}>
                                        <option value="Vacante">Vacante</option>
                                        <option value="Estadía">Estadía</option>
                                        <option value="Servicio Social">Servicio Social</option>
                                    </select>
                                </div>
                            </div>

                            <div className="space-y-1">
                                <label className="text-[10px] font-black uppercase ml-2 dark:text-white/50 tracking-widest">Requisitos (Separar por comas)</label>
                                <textarea required rows="2" className="w-full px-5 py-3 rounded-2xl bg-black/5 dark:bg-white/5 dark:text-white outline-none font-bold resize-none" value={formData.requirements} onChange={(e) => setFormData({...formData, requirements: e.target.value})} />
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-1">
                                    <label className="text-[10px] font-black uppercase ml-2 dark:text-white/50 tracking-widest">Nº Vacantes</label>
                                    <input type="number" className="w-full px-5 py-3 rounded-2xl bg-black/5 dark:bg-white/5 dark:text-white outline-none font-bold" value={formData.vacancies_count} onChange={(e) => setFormData({...formData, vacancies_count: e.target.value})} />
                                </div>
                                <div className="space-y-1">
                                    <label className="text-[10px] font-black uppercase ml-2 dark:text-white/50 tracking-widest flex items-center gap-1"><ImageIcon size={10}/> URL Imagen</label>
                                    <input 
                                        type="text" 
                                        placeholder="https://..."
                                        className="w-full px-5 py-3 rounded-2xl bg-black/5 dark:bg-white/5 dark:text-white outline-none font-bold text-xs" 
                                        value={formData.image_url} 
                                        onChange={(e) => setFormData({...formData, image_url: e.target.value})} 
                                    />
                                </div>
                            </div>

                            <button disabled={loading} type="submit" className="w-full bg-[#CE010B] text-white py-4 rounded-2xl font-black uppercase tracking-widest flex items-center justify-center gap-2 hover:bg-[#A30109] transition-all disabled:opacity-50">
                                {loading ? 'Procesando...' : <><Save size={20} /> {isEditing ? 'Actualizar' : 'Guardar'} Vacante</>}
                            </button>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminJobsPage;